<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import OnboardingAPI from 'dashboard/api/onboarding';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { isAdmin } = useAdmin();

const onboardingStatus = ref(null);
const isDismissed = ref(false);

const isOnboardingWizardEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.ONBOARDING_WIZARD)
);

// Non-blocking: agents can dismiss and keep using the tool. Only shown for
// not_started/in_progress — once an admin finishes or explicitly skips it, it never
// shows again for that account (ADR-002).
const shouldShow = computed(
  () =>
    !isDismissed.value &&
    isOnboardingWizardEnabled.value &&
    !isAdmin.value &&
    ['not_started', 'in_progress'].includes(onboardingStatus.value)
);

const dismiss = () => {
  isDismissed.value = true;
};

onMounted(() => {
  if (!isOnboardingWizardEnabled.value || isAdmin.value) return;

  OnboardingAPI.get()
    .then(({ data }) => {
      onboardingStatus.value = data.status;
    })
    .catch(() => {
      // Best-effort notice — failing silently here just keeps the modal hidden.
    });
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Modal
    v-if="shouldShow"
    :show="shouldShow"
    :on-close="dismiss"
    :close-on-backdrop-click="false"
    show-close-button
  >
    <div class="p-6">
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('ONBOARDING.PENDING_MODAL.TITLE') }}
      </h3>
      <p class="mt-2 text-n-slate-11">
        {{ t('ONBOARDING.PENDING_MODAL.MESSAGE') }}
      </p>
      <Button
        type="button"
        :label="t('ONBOARDING.PENDING_MODAL.DISMISS')"
        class="mt-4"
        @click="dismiss"
      />
    </div>
  </Modal>
</template>
